import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Search,
  SlidersHorizontal,
  X,
  RotateCcw,
  PackageSearch,
  Filter,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Monitor,
  Cpu,
  HardDrive,
  Layers,
  Wifi,
  BadgePercent,
} from 'lucide-react';
import { useCatalog } from '../../hooks/use-catalog';
import { ProductEntity } from '../../../domain/entities/product.entity';
import { CategoryEntity } from '../../../domain/entities/category.entity';
import { CatalogFilterOptions } from '../../../domain/repositories/catalog.repository';
import { ProductCard } from '../../components/ui/product-card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { SeoHead } from '../../components/seo/seo-head';

interface FilterSectionProps {
  id: string;
  title: string;
  icon?: React.ReactNode;
  selectedCount?: number;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const FilterSection: React.FC<FilterSectionProps> = ({
  title,
  icon,
  selectedCount = 0,
  isOpen,
  onToggle,
  children,
}) => (
  <div className="border-b border-neutral-200/80 dark:border-neutral-800/80 py-3.5 last:border-b-0">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between text-left py-1 text-xs font-bold uppercase tracking-wider text-neutral-800 dark:text-neutral-200 hover:text-brand-primary transition-colors cursor-pointer group"
    >
      <div className="flex items-center gap-2">
        {icon && <span className="text-brand-primary opacity-80">{icon}</span>}
        <span>{title}</span>
        {selectedCount > 0 && (
          <span className="ml-1.5 px-1.5 py-0.2 bg-brand-primary text-white text-[10px] font-bold rounded-full">
            {selectedCount}
          </span>
        )}
      </div>
      <ChevronDown
        className={`w-3.5 h-3.5 text-neutral-400 group-hover:text-neutral-700 dark:group-hover:text-neutral-200 transition-transform duration-200 ${
          isOpen ? 'rotate-180' : ''
        }`}
      />
    </button>
    {isOpen && <div className="pt-2.5 space-y-1">{children}</div>}
  </div>
);

export const CatalogoPage: React.FC = () => {
  const { getProductsUseCase, getCategoriesUseCase, getFilterOptionsUseCase, trackSearchUseCase } = useCatalog();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastTrackedSearchRef = useRef<string>('');

  const [products, setProducts] = useState<ProductEntity[]>([]);
  const [totalProducts, setTotalProducts] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(24);

  const [categories, setCategories] = useState<CategoryEntity[]>([]);
  const [filterOptions, setFilterOptions] = useState<CatalogFilterOptions>({
    subcategories: [],
    brands: [],
    operatingSystems: [],
    screenSizes: [],
    ramSizes: [],
    storageSizes: [],
    processors: [],
    connectivity: [],
    scannerTypes: [],
  });
  const [loading, setLoading] = useState(true);

  // Filtros derivados de la URL (Fuente de verdad para familias)
  const selectedCategory = searchParams.get('categoria') || 'all';
  const selectedSubcategory = searchParams.get('subcategoria') || 'all';

  // Estados de filtros adicionales
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [prevUrlQ, setPrevUrlQ] = useState(searchParams.get('q') || '');
  const currentUrlQ = searchParams.get('q') || '';
  if (currentUrlQ !== prevUrlQ) {
    setPrevUrlQ(currentUrlQ);
    setSearchQuery(currentUrlQ);
  }

  const [inPromotion, setInPromotion] = useState(searchParams.get('promocion') === 'true');
  const [prevPromoUrl, setPrevPromoUrl] = useState(searchParams.get('promocion') || '');
  const currentPromoUrl = searchParams.get('promocion') || '';
  if (currentPromoUrl !== prevPromoUrl) {
    setPrevPromoUrl(currentPromoUrl);
    setInPromotion(currentPromoUrl === 'true');
  }

  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedOS, setSelectedOS] = useState<string[]>([]);
  const [selectedScreens, setSelectedScreens] = useState<string[]>([]);
  const [selectedRAM, setSelectedRAM] = useState<string[]>([]);
  const [selectedStorage, setSelectedStorage] = useState<string[]>([]);
  const [selectedProcessors, setSelectedProcessors] = useState<string[]>([]);
  const [selectedConnectivity, setSelectedConnectivity] = useState<string[]>([]);

  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  // Secciones abiertas por defecto
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    categories: true,
    subcategories: true,
    os: true,
    ram: true,
    screens: true,
    storage: false,
    processors: false,
    brands: true,
    connectivity: false,
  });

  const toggleSection = (key: string) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Cargar categorías y opciones de filtro generales
  useEffect(() => {
    let isMounted = true;
    const loadCategories = async () => {
      try {
        const cats = await getCategoriesUseCase.execute();
        if (isMounted) setCategories(cats);
      } catch (err) {
        console.error('Error cargando categorías:', err);
      }
    };
    loadCategories();
    return () => {
      isMounted = false;
    };
  }, [getCategoriesUseCase]);

  // Actualizar opciones de filtros facetados según la categoría activa
  useEffect(() => {
    let isMounted = true;
    const loadOptions = async () => {
      try {
        const options = await getFilterOptionsUseCase.execute(
          selectedCategory !== 'all' ? selectedCategory : undefined
        );
        if (isMounted) setFilterOptions(options);
      } catch (err) {
        console.error('Error cargando opciones de filtro:', err);
      }
    };
    loadOptions();
    return () => {
      isMounted = false;
    };
  }, [selectedCategory, getFilterOptionsUseCase]);

  // Resetear a la página 1 cuando los filtros o itemsPerPage cambian
  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedCategory,
    selectedSubcategory,
    selectedBrands,
    selectedOS,
    selectedScreens,
    selectedRAM,
    selectedStorage,
    selectedProcessors,
    selectedConnectivity,
    searchQuery,
    itemsPerPage,
  ]);

  // Consultar productos aplicando todos los filtros técnicos facetados y paginación
  useEffect(() => {
    let isMounted = true;
    const fetchFilteredProducts = async () => {
      setLoading(true);
      try {
        const result = await getProductsUseCase.executePaginated({
          categorySlug: selectedCategory !== 'all' ? selectedCategory : undefined,
          subcategorySlug: selectedSubcategory !== 'all' ? selectedSubcategory : undefined,
          brands: selectedBrands.length > 0 ? selectedBrands : undefined,
          operatingSystems: selectedOS.length > 0 ? selectedOS : undefined,
          screenSizes: selectedScreens.length > 0 ? selectedScreens : undefined,
          ramSizes: selectedRAM.length > 0 ? selectedRAM : undefined,
          storageSizes: selectedStorage.length > 0 ? selectedStorage : undefined,
          processors: selectedProcessors.length > 0 ? selectedProcessors : undefined,
          connectivity: selectedConnectivity.length > 0 ? selectedConnectivity : undefined,
          search: searchQuery.trim() || undefined,
          inPromotion: inPromotion ? true : undefined,
          limit: itemsPerPage,
          page: currentPage,
        });

        if (isMounted) {
          setProducts(result.products);
          setTotalProducts(result.total);
          const trimmedQ = searchQuery.trim();
          if (trimmedQ.length >= 2 && trimmedQ !== lastTrackedSearchRef.current) {
            lastTrackedSearchRef.current = trimmedQ;
            trackSearchUseCase.execute(trimmedQ, result.total).catch(() => {});
          }
        }
      } catch (err) {
        console.error('Error filtrando productos:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchFilteredProducts();
    return () => {
      isMounted = false;
    };
  }, [
    selectedCategory,
    selectedSubcategory,
    selectedBrands,
    selectedOS,
    selectedScreens,
    selectedRAM,
    selectedStorage,
    selectedProcessors,
    selectedConnectivity,
    searchQuery,
    inPromotion,
    currentPage,
    itemsPerPage,
    getProductsUseCase,
    trackSearchUseCase,
  ]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = Math.max(1, Math.ceil(totalProducts / itemsPerPage));

  const getPaginationRange = (current: number, total: number) => {
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    if (current <= 4) {
      return [1, 2, 3, 4, 5, '...', total];
    }
    if (current >= total - 3) {
      return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
    }
    return [1, '...', current - 1, current, current + 1, '...', total];
  };

  // Manejo de selecciones
  const handleCategorySelect = (slug: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (slug === 'all') {
      newParams.delete('categoria');
      newParams.delete('subcategoria');
    } else {
      newParams.set('categoria', slug);
      newParams.delete('subcategoria');
    }
    setSearchParams(newParams);
  };

  const handleSubcategorySelect = (slug: string) => {
    const nextSub = selectedSubcategory === slug ? 'all' : slug;
    const newParams = new URLSearchParams(searchParams);
    if (nextSub === 'all') {
      newParams.delete('subcategoria');
    } else {
      newParams.set('subcategoria', nextSub);
    }
    setSearchParams(newParams);
  };

  const toggleFacet = (
    currentList: string[],
    itemValue: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    if (currentList.includes(itemValue)) {
      setter(currentList.filter((v) => v !== itemValue));
    } else {
      setter([...currentList, itemValue]);
    }
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setSelectedBrands([]);
    setSelectedOS([]);
    setSelectedScreens([]);
    setSelectedRAM([]);
    setSelectedStorage([]);
    setSelectedProcessors([]);
    setSelectedConnectivity([]);
    setInPromotion(false);
    setSearchParams({});
  };

  // Cálculo de filtros activos
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (selectedCategory !== 'all') count++;
    if (selectedSubcategory !== 'all') count++;
    if (inPromotion) count++;
    count += selectedBrands.length;
    count += selectedOS.length;
    count += selectedScreens.length;
    count += selectedRAM.length;
    count += selectedStorage.length;
    count += selectedProcessors.length;
    count += selectedConnectivity.length;
    if (searchQuery.trim()) count++;
    return count;
  }, [
    selectedCategory,
    selectedSubcategory,
    selectedBrands,
    selectedOS,
    selectedScreens,
    selectedRAM,
    selectedStorage,
    selectedProcessors,
    selectedConnectivity,
    searchQuery,
    inPromotion,
  ]);

  const activeCategoryObj = categories.find((c) => c.slug === selectedCategory);
  const activeSubcategoryObj = filterOptions.subcategories.find(
    (s) => s.value === selectedSubcategory
  );

  const brandTitle = selectedBrands.length > 0 ? `Marca ${selectedBrands.join(', ')}` : 'Hardware B2B y Retail';
  const categoryTitle = activeCategoryObj?.name || brandTitle;
  const catalogSeoTitle = `Catálogo de ${categoryTitle} | BARTECH Chile`;
  const catalogSeoDescription = activeCategoryObj?.description ||
    'Explora nuestro catálogo de hardware para retail, bodegas e industria en Chile: Scanners 1D/2D, PDA Honeywell, Lectores RFID, POS All-in-One y Balanzas. Cotiza online con soporte oficial.';

  const catalogJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': catalogSeoTitle,
    'description': catalogSeoDescription,
    'url': 'https://bartech.cl/catalogo',
    'mainEntity': {
      '@type': 'ItemList',
      'itemListElement': products.slice(0, 12).map((p, idx) => ({
        '@type': 'ListItem',
        'position': idx + 1,
        'url': `https://bartech.cl/catalogo/${p.slug}`,
        'name': p.name,
      })),
    },
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <SeoHead
        title={catalogSeoTitle}
        description={catalogSeoDescription}
        keywords="catalogo hardware retail, comprar pda chile, distribuidores honeywell santiago, terminales pos chile, lectores rfid, contadoras de billetes"
        canonicalUrl="https://bartech.cl/catalogo"
        jsonLd={catalogJsonLd}
      />
      {/* Header del Catálogo */}
      <div className="border-b border-neutral-200 dark:border-neutral-800 pb-6 mb-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="brand" size="sm">
                Catálogo de Equipos y Hardware
              </Badge>
              <span className="text-xs text-neutral-400">•</span>
              <span className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">
                Venta para Empresas y Particulares
              </span>
            </div>
            <h1 className="text-2xl sm:text-4xl font-black text-neutral-900 dark:text-white tracking-tight">
              Catálogo de Hardware & Soluciones POS
            </h1>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1 max-w-2xl">
              Equipamiento técnico para puntos de venta, terminales táctiles, lectura de código de barras, impresión térmica y captura de datos.
            </p>
          </div>

          {/* Contador, Selector de Paginación y Gatillador de Filtros Mobile */}
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">
              {totalProducts > 0
                ? `Mostrando ${(currentPage - 1) * itemsPerPage + 1} - ${Math.min(
                    currentPage * itemsPerPage,
                    totalProducts
                  )} de ${totalProducts} equipos`
                : '0 equipos encontrados'}
            </span>

            {/* Selector de cantidad por página */}
            <div className="flex items-center gap-1.5 text-xs text-neutral-600 dark:text-neutral-400 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 px-2.5 py-1.5 rounded-xl shadow-xs">
              <span className="hidden sm:inline font-medium">Por pág:</span>
              <select
                aria-label="Equipos por página"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="bg-transparent text-xs font-bold text-neutral-900 dark:text-white focus:outline-none cursor-pointer"
              >
                <option value={12}>12</option>
                <option value={24}>24</option>
                <option value={48}>48</option>
                <option value={60}>60</option>
                <option value={96}>96</option>
              </select>
            </div>

            <button
              type="button"
              onClick={() => setMobileFilterOpen(true)}
              className="md:hidden flex items-center gap-2 px-3 py-2 rounded-xl bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 text-xs font-bold text-neutral-800 dark:text-neutral-200 shadow-sm cursor-pointer"
            >
              <SlidersHorizontal className="w-3.5 h-3.5 text-brand-primary" />
              <span>Filtros</span>
              {activeFiltersCount > 0 && (
                <span className="px-1.5 py-0.2 bg-brand-primary text-white text-[10px] font-bold rounded-full">
                  {activeFiltersCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Buscador de Hardware Principal */}
        <div className="mt-6 relative max-w-2xl">
          <Search className="w-4 h-4 text-neutral-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por modelo, RAM, procesador, tamaño de pantalla o código (ej. i5, 8GB, 15.6, Honeywell)..."
            className="w-full pl-10 pr-10 py-2.5 bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 rounded-xl text-sm placeholder-neutral-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary transition-all text-neutral-900 dark:text-white shadow-inner"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Barra de Filtros Activos (Pills) */}
        {activeFiltersCount > 0 && (
          <div className="mt-4 pt-4 border-t border-neutral-200/60 dark:border-neutral-800/60 flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold text-neutral-500 uppercase tracking-wider mr-1">
              Filtros activos:
            </span>

            {/* Búsqueda */}
            {searchQuery && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 border border-neutral-200 dark:border-neutral-700">
                Texto: "{searchQuery}"
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="hover:text-brand-primary"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}

            {/* Categoría */}
            {selectedCategory !== 'all' && activeCategoryObj && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-brand-50 dark:bg-brand-950/40 text-brand-primary border border-brand-200 dark:border-brand-800/60">
                Familia: {activeCategoryObj.name}
                <button
                  type="button"
                  onClick={() => handleCategorySelect('all')}
                  className="hover:text-brand-700"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}

            {/* Subcategoría */}
            {selectedSubcategory !== 'all' && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-brand-50 dark:bg-brand-950/40 text-brand-primary border border-brand-200 dark:border-brand-800/60">
                Subfamilia: {activeSubcategoryObj?.label || selectedSubcategory}
                <button
                  type="button"
                  onClick={() => handleSubcategorySelect(selectedSubcategory)}
                  className="hover:text-brand-700"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}

            {/* Marcas */}
            {selectedBrands.map((b) => (
              <span
                key={b}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 border border-neutral-200 dark:border-neutral-700"
              >
                Marca: {b}
                <button
                  type="button"
                  onClick={() => toggleFacet(selectedBrands, b, setSelectedBrands)}
                  className="hover:text-brand-primary"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}

            {/* Sistema Operativo */}
            {selectedOS.map((os) => (
              <span
                key={os}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800"
              >
                SO: {os}
                <button
                  type="button"
                  onClick={() => toggleFacet(selectedOS, os, setSelectedOS)}
                  className="hover:text-emerald-900"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}

            {/* Pantalla */}
            {selectedScreens.map((sc) => (
              <span
                key={sc}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
              >
                Pantalla: {sc}
                <button
                  type="button"
                  onClick={() => toggleFacet(selectedScreens, sc, setSelectedScreens)}
                  className="hover:text-blue-900"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}

            {/* RAM */}
            {selectedRAM.map((r) => (
              <span
                key={r}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800"
              >
                RAM: {r}
                <button
                  type="button"
                  onClick={() => toggleFacet(selectedRAM, r, setSelectedRAM)}
                  className="hover:text-purple-900"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}

            {/* Almacenamiento */}
            {selectedStorage.map((st) => (
              <span
                key={st}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 border border-neutral-200 dark:border-neutral-700"
              >
                Almacenamiento: {st}
                <button
                  type="button"
                  onClick={() => toggleFacet(selectedStorage, st, setSelectedStorage)}
                  className="hover:text-brand-primary"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}

            {/* Procesador */}
            {selectedProcessors.map((p) => (
              <span
                key={p}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 border border-neutral-200 dark:border-neutral-700"
              >
                CPU: {p}
                <button
                  type="button"
                  onClick={() => toggleFacet(selectedProcessors, p, setSelectedProcessors)}
                  className="hover:text-brand-primary"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}

            {/* Conectividad */}
            {selectedConnectivity.map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 border border-neutral-200 dark:border-neutral-700"
              >
                {c}
                <button
                  type="button"
                  onClick={() => toggleFacet(selectedConnectivity, c, setSelectedConnectivity)}
                  className="hover:text-brand-primary"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}

            {inPromotion && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60 shadow-xs">
                <BadgePercent className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                <span>En Promoción</span>
                <button
                  type="button"
                  onClick={() => {
                    setInPromotion(false);
                    setSearchParams((prev) => {
                      const next = new URLSearchParams(prev);
                      next.delete('promocion');
                      return next;
                    });
                  }}
                  className="hover:text-amber-900 dark:hover:text-white cursor-pointer ml-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}

            <button
              type="button"
              onClick={handleResetFilters}
              className="text-xs font-bold text-brand-primary hover:underline flex items-center gap-1 ml-2 cursor-pointer"
            >
              <RotateCcw className="w-3 h-3" />
              Limpiar todos los filtros
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-5 gap-8">
        {/* Sidebar de Filtros Desktop */}
        <aside className="hidden md:block md:col-span-1 space-y-4">
          <div className="bg-white dark:bg-neutral-900 p-5 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm sticky top-28 max-h-[82vh] overflow-y-auto pr-3">
            <div className="flex items-center justify-between pb-3 border-b border-neutral-200/80 dark:border-neutral-800/80 mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-neutral-900 dark:text-white flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5 text-brand-primary" />
                Filtros Técnicos
              </span>
              {activeFiltersCount > 0 && (
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="text-[11px] font-semibold text-brand-primary hover:underline flex items-center gap-1 cursor-pointer"
                >
                  <RotateCcw className="w-3 h-3" />
                  Limpiar ({activeFiltersCount})
                </button>
              )}
            </div>

            {/* 1. Categoría Principal */}
            <FilterSection
              id="categories"
              title="Familia de Hardware"
              icon={<Layers className="w-3.5 h-3.5" />}
              selectedCount={selectedCategory !== 'all' ? 1 : 0}
              isOpen={openSections.categories}
              onToggle={() => toggleSection('categories')}
            >
              <button
                type="button"
                onClick={() => handleCategorySelect('all')}
                className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center justify-between cursor-pointer ${
                  selectedCategory === 'all'
                    ? 'bg-brand-primary text-white font-semibold shadow-sm'
                    : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                }`}
              >
                <span>Todas las familias</span>
                <span className="text-[10px] opacity-75 font-mono">
                  ({categories.reduce((acc, c) => acc + (c.itemCount || 0), 0)})
                </span>
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => handleCategorySelect(cat.slug)}
                  className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center justify-between cursor-pointer ${
                    selectedCategory === cat.slug
                      ? 'bg-brand-primary text-white font-semibold shadow-sm'
                      : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                  }`}
                >
                  <span className="truncate mr-2">{cat.name}</span>
                  <span className="text-[10px] opacity-75 font-mono">({cat.itemCount})</span>
                </button>
              ))}
            </FilterSection>

            {/* 2. Subcategorías (Si hay disponibles para la categoría o en general) */}
            {filterOptions.subcategories.length > 0 && (
              <FilterSection
                id="subcategories"
                title="Subfamilias / Tipo"
                selectedCount={selectedSubcategory !== 'all' ? 1 : 0}
                isOpen={openSections.subcategories}
                onToggle={() => toggleSection('subcategories')}
              >
                {filterOptions.subcategories.map((sub) => {
                  const isChecked = selectedSubcategory === sub.value;
                  return (
                    <button
                      key={sub.value}
                      type="button"
                      onClick={() => handleSubcategorySelect(sub.value)}
                      className={`w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center justify-between cursor-pointer ${
                        isChecked
                          ? 'bg-brand-50 dark:bg-brand-950/50 text-brand-primary font-bold border border-brand-200 dark:border-brand-800'
                          : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                      }`}
                    >
                      <span className="truncate mr-2">{sub.label}</span>
                      <span className="text-[10px] text-neutral-400 font-mono">({sub.count})</span>
                    </button>
                  );
                })}
              </FilterSection>
            )}

            {/* Filtro Promoción (Checkbox ubicado arriba de Sistema Operativo) */}
            <div className="py-2.5 border-b border-neutral-200/80 dark:border-neutral-800/80">
              <label className="flex items-center justify-between px-2.5 py-2 rounded-xl text-xs font-semibold text-neutral-800 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800/60 transition-colors cursor-pointer group">
                <div className="flex items-center gap-2.5">
                  <input
                    type="checkbox"
                    checked={inPromotion}
                    onChange={(e) => {
                      const val = e.target.checked;
                      setInPromotion(val);
                      setSearchParams((prev) => {
                        const next = new URLSearchParams(prev);
                        if (val) next.set('promocion', 'true');
                        else next.delete('promocion');
                        return next;
                      });
                    }}
                    className="rounded border-neutral-300 text-brand-primary focus:ring-brand-primary w-4 h-4 cursor-pointer"
                  />
                  <span className={`flex items-center gap-1.5 ${inPromotion ? 'font-bold text-brand-primary' : ''}`}>
                    <BadgePercent className="w-4 h-4 text-brand-primary" />
                    Promoción
                  </span>
                </div>
                {inPromotion && (
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                )}
              </label>
            </div>

            {/* 3. Sistema Operativo */}
            {filterOptions.operatingSystems.length > 0 && (
              <FilterSection
                id="os"
                title="Sistema Operativo"
                selectedCount={selectedOS.length}
                isOpen={openSections.os}
                onToggle={() => toggleSection('os')}
              >
                {filterOptions.operatingSystems.map((item) => {
                  const isChecked = selectedOS.includes(item.value);
                  return (
                    <label
                      key={item.value}
                      className="flex items-center justify-between px-2 py-1.5 rounded-lg text-xs text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleFacet(selectedOS, item.value, setSelectedOS)}
                          className="rounded border-neutral-300 text-brand-primary focus:ring-brand-primary w-3.5 h-3.5"
                        />
                        <span className={isChecked ? 'font-bold text-brand-primary' : ''}>
                          {item.label}
                        </span>
                      </div>
                      <span className="text-[10px] text-neutral-400">({item.count})</span>
                    </label>
                  );
                })}
              </FilterSection>
            )}

            {/* 4. Memoria RAM */}
            {filterOptions.ramSizes.length > 0 && (
              <FilterSection
                id="ram"
                title="Memoria RAM"
                selectedCount={selectedRAM.length}
                isOpen={openSections.ram}
                onToggle={() => toggleSection('ram')}
              >
                {filterOptions.ramSizes.map((item) => {
                  const isChecked = selectedRAM.includes(item.value);
                  return (
                    <label
                      key={item.value}
                      className="flex items-center justify-between px-2 py-1.5 rounded-lg text-xs text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleFacet(selectedRAM, item.value, setSelectedRAM)}
                          className="rounded border-neutral-300 text-brand-primary focus:ring-brand-primary w-3.5 h-3.5"
                        />
                        <span className={isChecked ? 'font-bold text-brand-primary' : ''}>
                          {item.label}
                        </span>
                      </div>
                      <span className="text-[10px] text-neutral-400">({item.count})</span>
                    </label>
                  );
                })}
              </FilterSection>
            )}

            {/* 5. Tamaño de Pantalla */}
            {filterOptions.screenSizes.length > 0 && (
              <FilterSection
                id="screens"
                title="Tamaño de Pantalla"
                icon={<Monitor className="w-3.5 h-3.5" />}
                selectedCount={selectedScreens.length}
                isOpen={openSections.screens}
                onToggle={() => toggleSection('screens')}
              >
                {filterOptions.screenSizes.map((item) => {
                  const isChecked = selectedScreens.includes(item.value);
                  return (
                    <label
                      key={item.value}
                      className="flex items-center justify-between px-2 py-1.5 rounded-lg text-xs text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() =>
                            toggleFacet(selectedScreens, item.value, setSelectedScreens)
                          }
                          className="rounded border-neutral-300 text-brand-primary focus:ring-brand-primary w-3.5 h-3.5"
                        />
                        <span className={isChecked ? 'font-bold text-brand-primary' : ''}>
                          {item.label}
                        </span>
                      </div>
                      <span className="text-[10px] text-neutral-400">({item.count})</span>
                    </label>
                  );
                })}
              </FilterSection>
            )}

            {/* 6. Almacenamiento */}
            {filterOptions.storageSizes.length > 0 && (
              <FilterSection
                id="storage"
                title="Almacenamiento (ROM/SSD)"
                icon={<HardDrive className="w-3.5 h-3.5" />}
                selectedCount={selectedStorage.length}
                isOpen={openSections.storage}
                onToggle={() => toggleSection('storage')}
              >
                {filterOptions.storageSizes.map((item) => {
                  const isChecked = selectedStorage.includes(item.value);
                  return (
                    <label
                      key={item.value}
                      className="flex items-center justify-between px-2 py-1.5 rounded-lg text-xs text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() =>
                            toggleFacet(selectedStorage, item.value, setSelectedStorage)
                          }
                          className="rounded border-neutral-300 text-brand-primary focus:ring-brand-primary w-3.5 h-3.5"
                        />
                        <span className={isChecked ? 'font-bold text-brand-primary' : ''}>
                          {item.label}
                        </span>
                      </div>
                      <span className="text-[10px] text-neutral-400">({item.count})</span>
                    </label>
                  );
                })}
              </FilterSection>
            )}

            {/* 7. Procesador */}
            {filterOptions.processors.length > 0 && (
              <FilterSection
                id="processors"
                title="Procesador / CPU"
                icon={<Cpu className="w-3.5 h-3.5" />}
                selectedCount={selectedProcessors.length}
                isOpen={openSections.processors}
                onToggle={() => toggleSection('processors')}
              >
                {filterOptions.processors.map((item) => {
                  const isChecked = selectedProcessors.includes(item.value);
                  return (
                    <label
                      key={item.value}
                      className="flex items-center justify-between px-2 py-1.5 rounded-lg text-xs text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() =>
                            toggleFacet(selectedProcessors, item.value, setSelectedProcessors)
                          }
                          className="rounded border-neutral-300 text-brand-primary focus:ring-brand-primary w-3.5 h-3.5"
                        />
                        <span className={isChecked ? 'font-bold text-brand-primary' : ''}>
                          {item.label}
                        </span>
                      </div>
                      <span className="text-[10px] text-neutral-400">({item.count})</span>
                    </label>
                  );
                })}
              </FilterSection>
            )}

            {/* 8. Marcas */}
            {filterOptions.brands.length > 0 && (
              <FilterSection
                id="brands"
                title="Marca de Fabricante"
                selectedCount={selectedBrands.length}
                isOpen={openSections.brands}
                onToggle={() => toggleSection('brands')}
              >
                {filterOptions.brands.map((item) => {
                  const isChecked = selectedBrands.includes(item.value);
                  return (
                    <label
                      key={item.value}
                      className="flex items-center justify-between px-2 py-1.5 rounded-lg text-xs text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleFacet(selectedBrands, item.value, setSelectedBrands)}
                          className="rounded border-neutral-300 text-brand-primary focus:ring-brand-primary w-3.5 h-3.5"
                        />
                        <span className={isChecked ? 'font-bold text-brand-primary' : ''}>
                          {item.label}
                        </span>
                      </div>
                      <span className="text-[10px] text-neutral-400">({item.count})</span>
                    </label>
                  );
                })}
              </FilterSection>
            )}

            {/* 9. Conectividad e Interfaces */}
            {filterOptions.connectivity.length > 0 && (
              <FilterSection
                id="connectivity"
                title="Conectividad & Red"
                icon={<Wifi className="w-3.5 h-3.5" />}
                selectedCount={selectedConnectivity.length}
                isOpen={openSections.connectivity}
                onToggle={() => toggleSection('connectivity')}
              >
                {filterOptions.connectivity.map((item) => {
                  const isChecked = selectedConnectivity.includes(item.value);
                  return (
                    <label
                      key={item.value}
                      className="flex items-center justify-between px-2 py-1.5 rounded-lg text-xs text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() =>
                            toggleFacet(selectedConnectivity, item.value, setSelectedConnectivity)
                          }
                          className="rounded border-neutral-300 text-brand-primary focus:ring-brand-primary w-3.5 h-3.5"
                        />
                        <span className={isChecked ? 'font-bold text-brand-primary' : ''}>
                          {item.label}
                        </span>
                      </div>
                      <span className="text-[10px] text-neutral-400">({item.count})</span>
                    </label>
                  );
                })}
              </FilterSection>
            )}
          </div>
        </aside>

        {/* Grid de Productos */}
        <section className="col-span-1 md:col-span-3 lg:col-span-4">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-80 bg-neutral-200 dark:bg-neutral-800 rounded-2xl" />
              ))}
            </div>
          ) : products.length === 0 ? (
            activeFiltersCount === 0 && !searchQuery.trim() ? (
              <div className="text-center py-20 px-6 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl shadow-sm">
                <PackageSearch className="w-14 h-14 text-neutral-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-neutral-900 dark:text-white">
                  Catálogo de Equipos Vacío
                </h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 max-w-md mx-auto mt-2 mb-6 leading-relaxed">
                  Actualmente no hay equipos registrados en la plataforma. Puedes registrar tus primeros modelos, marcas y categorías desde el Panel de Administración.
                </p>
                <Link to="/login">
                  <Button variant="primary" size="md">
                    Ir al Panel de Administración
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="text-center py-20 px-6 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl shadow-sm">
                <PackageSearch className="w-14 h-14 text-neutral-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-neutral-900 dark:text-white">
                  No se encontraron equipos con los criterios seleccionados
                </h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 max-w-md mx-auto mt-2 mb-6 leading-relaxed">
                  Ningún equipo coincide exactamente con los filtros aplicados (pantalla, RAM, sistema operativo, etc.). Prueba ampliando tus criterios.
                </p>
                <Button variant="secondary" onClick={handleResetFilters} size="md">
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Restablecer todos los filtros
                </Button>
              </div>
            )
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>

              {/* Controles de Paginación */}
              {totalPages > 1 && (
                <div className="mt-12 pt-6 border-t border-neutral-200 dark:border-neutral-800 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">
                    Página <span className="font-bold text-neutral-900 dark:text-white">{currentPage}</span> de{' '}
                    <span className="font-bold text-neutral-900 dark:text-white">{totalPages}</span> ({totalProducts} productos)
                  </div>

                  <div className="flex items-center gap-1.5">
                    {/* Primera página */}
                    <button
                      type="button"
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                      title="Primera página"
                      className="p-2 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors shadow-xs"
                    >
                      <ChevronsLeft className="w-4 h-4" />
                    </button>

                    {/* Anterior */}
                    <button
                      type="button"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      title="Página anterior"
                      className="px-3 py-2 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 text-xs font-semibold hover:bg-neutral-100 dark:hover:bg-neutral-700 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors flex items-center gap-1 shadow-xs"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Anterior</span>
                    </button>

                    {/* Números de página */}
                    <div className="flex items-center gap-1">
                      {getPaginationRange(currentPage, totalPages).map((item, idx) => {
                        if (item === '...') {
                          return (
                            <span
                              key={`ellipsis-${idx}`}
                              className="px-2 py-1 text-xs text-neutral-400 font-bold select-none"
                            >
                              …
                            </span>
                          );
                        }

                        const pageNum = Number(item);
                        const isActive = pageNum === currentPage;
                        return (
                          <button
                            key={pageNum}
                            type="button"
                            onClick={() => handlePageChange(pageNum)}
                            className={`min-w-9 h-9 px-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                              isActive
                                ? 'bg-[#33CC00] text-white shadow-md shadow-[#33CC00]/30 scale-105 font-black'
                                : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 border border-neutral-200 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    {/* Siguiente */}
                    <button
                      type="button"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      title="Página siguiente"
                      className="px-3 py-2 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 text-xs font-semibold hover:bg-neutral-100 dark:hover:bg-neutral-700 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors flex items-center gap-1 shadow-xs"
                    >
                      <span className="hidden sm:inline">Siguiente</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>

                    {/* Última página */}
                    <button
                      type="button"
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                      title="Última página"
                      className="p-2 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors shadow-xs"
                    >
                      <ChevronsRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {/* Bottom Sheet / Drawer de Filtros en Mobile */}
      {mobileFilterOpen && (
        <div className="fixed inset-0 z-50 md:hidden flex flex-col justify-end bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-neutral-900 rounded-t-3xl p-6 max-h-[85vh] overflow-y-auto space-y-6 animate-in slide-in-from-bottom duration-200 shadow-2xl">
            <div className="flex items-center justify-between border-b border-neutral-200 dark:border-neutral-800 pb-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-brand-primary" />
                <h3 className="text-base font-bold text-neutral-900 dark:text-white">
                  Filtros de Hardware ({activeFiltersCount})
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setMobileFilterOpen(false)}
                className="p-1.5 rounded-lg text-neutral-500 hover:text-neutral-900 dark:hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Categorías Mobile */}
            <div>
              <h4 className="text-xs font-bold text-neutral-500 uppercase tracking-wider mb-2">
                Familia de Hardware
              </h4>
              <div className="grid grid-cols-1 gap-1.5">
                <button
                  type="button"
                  onClick={() => handleCategorySelect('all')}
                  className={`p-2.5 rounded-xl text-left text-xs font-semibold ${
                    selectedCategory === 'all'
                      ? 'bg-brand-primary text-white'
                      : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200'
                  }`}
                >
                  Todas las familias
                </button>
                {categories.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => handleCategorySelect(c.slug)}
                    className={`p-2.5 rounded-xl text-left text-xs font-semibold flex items-center justify-between ${
                      selectedCategory === c.slug
                        ? 'bg-brand-primary text-white'
                        : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200'
                    }`}
                  >
                    <span>{c.name}</span>
                    <span className="text-[10px] opacity-75">({c.itemCount})</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Subcategorías Mobile */}
            {filterOptions.subcategories.length > 0 && (
              <div>
                <h4 className="text-xs font-bold text-neutral-500 uppercase tracking-wider mb-2">
                  Subfamilia
                </h4>
                <div className="grid grid-cols-1 gap-1.5">
                  {filterOptions.subcategories.map((sub) => (
                    <button
                      key={sub.value}
                      type="button"
                      onClick={() => handleSubcategorySelect(sub.value)}
                      className={`p-2.5 rounded-xl text-left text-xs font-semibold flex items-center justify-between ${
                        selectedSubcategory === sub.value
                          ? 'bg-brand-primary text-white'
                          : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200'
                      }`}
                    >
                      <span>{sub.label}</span>
                      <span className="text-[10px] opacity-75">({sub.count})</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Filtro Promoción Mobile */}
            <div>
              <label className="flex items-center justify-between p-3 rounded-xl bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200 cursor-pointer">
                <div className="flex items-center gap-2.5">
                  <input
                    type="checkbox"
                    checked={inPromotion}
                    onChange={(e) => {
                      const val = e.target.checked;
                      setInPromotion(val);
                      setSearchParams((prev) => {
                        const next = new URLSearchParams(prev);
                        if (val) next.set('promocion', 'true');
                        else next.delete('promocion');
                        return next;
                      });
                    }}
                    className="rounded border-neutral-300 text-brand-primary focus:ring-brand-primary w-4 h-4 cursor-pointer"
                  />
                  <span className={`text-xs flex items-center gap-1.5 ${inPromotion ? 'font-bold text-brand-primary' : 'font-semibold'}`}>
                    <BadgePercent className="w-4 h-4 text-brand-primary" />
                    En Promoción
                  </span>
                </div>
                {inPromotion && (
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                )}
              </label>
            </div>

            {/* Sistema Operativo Mobile */}
            {filterOptions.operatingSystems.length > 0 && (
              <div>
                <h4 className="text-xs font-bold text-neutral-500 uppercase tracking-wider mb-2">
                  Sistema Operativo
                </h4>
                <div className="grid grid-cols-2 gap-1.5">
                  {filterOptions.operatingSystems.map((item) => {
                    const isChecked = selectedOS.includes(item.value);
                    return (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() => toggleFacet(selectedOS, item.value, setSelectedOS)}
                        className={`p-2.5 rounded-xl text-xs font-semibold flex items-center justify-between ${
                          isChecked
                            ? 'bg-brand-primary text-white'
                            : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200'
                        }`}
                      >
                        <span className="truncate">{item.label}</span>
                        <span className="text-[10px] opacity-75">({item.count})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Memoria RAM Mobile */}
            {filterOptions.ramSizes.length > 0 && (
              <div>
                <h4 className="text-xs font-bold text-neutral-500 uppercase tracking-wider mb-2">
                  Memoria RAM
                </h4>
                <div className="grid grid-cols-3 gap-1.5">
                  {filterOptions.ramSizes.map((item) => {
                    const isChecked = selectedRAM.includes(item.value);
                    return (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() => toggleFacet(selectedRAM, item.value, setSelectedRAM)}
                        className={`p-2.5 rounded-xl text-xs font-semibold flex items-center justify-between ${
                          isChecked
                            ? 'bg-brand-primary text-white'
                            : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200'
                        }`}
                      >
                        <span>{item.label}</span>
                        <span className="text-[10px] opacity-75">({item.count})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tamaño de Pantalla Mobile */}
            {filterOptions.screenSizes.length > 0 && (
              <div>
                <h4 className="text-xs font-bold text-neutral-500 uppercase tracking-wider mb-2">
                  Tamaño de Pantalla
                </h4>
                <div className="grid grid-cols-3 gap-1.5">
                  {filterOptions.screenSizes.map((item) => {
                    const isChecked = selectedScreens.includes(item.value);
                    return (
                      <button
                        key={item.value}
                        type="button"
                        onClick={() => toggleFacet(selectedScreens, item.value, setSelectedScreens)}
                        className={`p-2.5 rounded-xl text-xs font-semibold flex items-center justify-between ${
                          isChecked
                            ? 'bg-brand-primary text-white'
                            : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-200'
                        }`}
                      >
                        <span>{item.label}</span>
                        <span className="text-[10px] opacity-75">({item.count})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Botones de acción del Mobile Drawer */}
            <div className="pt-4 border-t border-neutral-200 dark:border-neutral-800 flex gap-3 sticky bottom-0 bg-white dark:bg-neutral-900 pb-2">
              <Button variant="outline" className="flex-1 py-3" onClick={handleResetFilters}>
                Limpiar Todo
              </Button>
              <Button
                variant="primary"
                className="flex-1 py-3"
                onClick={() => setMobileFilterOpen(false)}
              >
                Ver {totalProducts} {totalProducts === 1 ? 'equipo' : 'equipos'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
